/**
 * Payroll Calculator Utility
 *
 * Provides frontend preview calculations for payroll statutory contributions.
 * Note: These are PREVIEW calculations only. Final calculations should always
 * be performed by the backend API for accuracy and compliance.
 */

class PayrollCalculator {

    /**
     * Calculate EPF contributions (preview only)
     * @param {number} monthlySalary - Monthly salary amount
     * @param {number} employeeAge - Employee age
     * @param {boolean} isOptedOut - Has employee opted out (age 55-60)
     * @returns {object} EPF calculation result
     */
    calculateEPFPreview(monthlySalary, employeeAge, isOptedOut = false) {
        const result = {
            monthlySalary: monthlySalary,
            employeeAge: employeeAge,
            isEligible: true,
            reason: '',
            employeeRate: 0,
            employerRate: 0,
            employeeContribution: 0,
            employerContribution: 0
        };

        // EPF eligibility rules
        if (employeeAge >= 60) {
            result.isEligible = false;
            result.reason = 'Employee is 60 years or older';
            return result;
        }

        if (employeeAge >= 55 && isOptedOut) {
            result.isEligible = false;
            result.reason = 'Employee has opted out (age 55-60)';
            return result;
        }

        // EPF contribution ceiling: RM 4,000
        const contributionBase = Math.min(monthlySalary, 4000);

        // Standard EPF rates (2026)
        // Employee: 11%
        // Employer: 12% (salary below RM 5,000), 13% (salary RM 5,000 and above)
        result.employeeRate = 0.11;
        result.employerRate = monthlySalary < 5000 ? 0.12 : 0.13;

        result.employeeContribution = Math.round(contributionBase * result.employeeRate * 100) / 100;
        result.employerContribution = Math.round(contributionBase * result.employerRate * 100) / 100;

        return result;
    }

    /**
     * Calculate SOCSO contributions (preview only)
     * Simplified calculation - actual SOCSO uses wage bracket tables
     * @param {number} monthlySalary - Monthly salary amount
     * @param {number} employeeAge - Employee age
     * @returns {object} SOCSO calculation result
     */
    calculateSOCSOPreview(monthlySalary, employeeAge) {
        const result = {
            monthlySalary: monthlySalary,
            employeeAge: employeeAge,
            isEligible: true,
            reason: '',
            employeeContribution: 0,
            employerContribution: 0
        };

        // SOCSO eligibility rules
        if (employeeAge >= 60) {
            result.isEligible = false;
            result.reason = 'Employee is 60 years or older';
            return result;
        }

        // SOCSO wage ceiling: RM 4,000
        if (monthlySalary > 4000) {
            result.reason = 'Salary exceeds RM 4,000, using maximum contribution';
        }

        // Simplified SOCSO calculation (actual uses wage bracket tables)
        // This is approximate - backend will use exact tables
        const cappedSalary = Math.min(monthlySalary, 4000);

        // Approximate rates: Employee 0.5%, Employer 1.75%
        result.employeeContribution = Math.round(cappedSalary * 0.005 * 100) / 100;
        result.employerContribution = Math.round(cappedSalary * 0.0175 * 100) / 100;

        return result;
    }

    /**
     * Calculate EIS contributions (preview only)
     * Uses official EIS contribution table (table-based, not percentage)
     * Source: https://payroll.my/payroll-software/eis-contribution-rates
     * @param {number} monthlySalary - Monthly salary amount
     * @returns {object} EIS calculation result
     */
    calculateEISPreview(monthlySalary) {
        const result = {
            monthlySalary: monthlySalary,
            wageCeiling: 6000,
            employeeContribution: 0,
            employerContribution: 0
        };

        // EIS uses a wage bracket table, not a percentage
        // Both employee and employer contributions are equal
        const eisAmount = this.getEISAmount(monthlySalary);

        result.employeeContribution = eisAmount;
        result.employerContribution = eisAmount;

        return result;
    }

    /**
     * Get EIS contribution amount from wage bracket table
     * Wage ceiling: RM 6,000 (effective October 2024)
     * @param {number} salary - Monthly salary amount
     * @returns {number} EIS contribution amount
     */
    getEISAmount(salary) {
        if (salary <= 30) return 0.05;
        if (salary <= 50) return 0.10;
        if (salary <= 70) return 0.15;
        if (salary <= 100) return 0.20;
        if (salary <= 140) return 0.25;
        if (salary <= 200) return 0.35;
        if (salary <= 300) return 0.50;
        if (salary <= 400) return 0.70;
        if (salary <= 500) return 0.90;
        if (salary <= 600) return 1.10;
        if (salary <= 700) return 1.30;
        if (salary <= 800) return 1.50;
        if (salary <= 900) return 1.70;
        if (salary <= 1000) return 1.90;
        if (salary <= 1100) return 2.10;
        if (salary <= 1200) return 2.30;
        if (salary <= 1300) return 2.50;
        if (salary <= 1400) return 2.70;
        if (salary <= 1500) return 2.90;
        if (salary <= 1600) return 3.10;
        if (salary <= 1700) return 3.30;
        if (salary <= 1800) return 3.50;
        if (salary <= 1900) return 3.70;
        if (salary <= 2000) return 3.90;
        if (salary <= 2100) return 4.10;
        if (salary <= 2200) return 4.30;
        if (salary <= 2300) return 4.50;
        if (salary <= 2400) return 4.70;
        if (salary <= 2500) return 4.90;
        if (salary <= 2600) return 5.10;
        if (salary <= 2700) return 5.30;
        if (salary <= 2800) return 5.50;
        if (salary <= 2900) return 5.70;
        if (salary <= 3000) return 5.90;
        if (salary <= 3100) return 6.10;
        if (salary <= 3200) return 6.30;
        if (salary <= 3300) return 6.50;
        if (salary <= 3400) return 6.70;
        if (salary <= 3500) return 6.90;
        if (salary <= 3600) return 7.10;
        if (salary <= 3700) return 7.30;
        if (salary <= 3800) return 7.50;
        if (salary <= 3900) return 7.70;
        if (salary <= 4000) return 7.90;
        if (salary <= 4100) return 8.10;
        if (salary <= 4200) return 8.30;
        if (salary <= 4300) return 8.50;
        if (salary <= 4400) return 8.70;
        if (salary <= 4500) return 8.90;
        if (salary <= 4600) return 9.10;
        if (salary <= 4700) return 9.30;
        if (salary <= 4800) return 9.50;
        if (salary <= 4900) return 9.70;
        if (salary <= 5000) return 9.90;
        if (salary <= 5100) return 10.10;
        if (salary <= 5200) return 10.30;
        if (salary <= 5300) return 10.50;
        if (salary <= 5400) return 10.70;
        if (salary <= 5500) return 10.90;
        if (salary <= 5600) return 11.10;
        if (salary <= 5700) return 11.30;
        if (salary <= 5800) return 11.50;
        if (salary <= 5900) return 11.70;
        if (salary <= 6000) return 11.90;
        return 11.90; // Above RM6000 (capped at RM11.90)
    }

    /**
     * Calculate PCB (Monthly Tax Deduction) - simplified preview
     * @param {number} monthlyIncome - Monthly income amount
     * @param {object} taxReliefs - Tax relief amounts
     * @returns {object} PCB calculation result
     */
    calculatePCBPreview(monthlyIncome, taxReliefs = null) {
        const result = {
            monthlyIncome: monthlyIncome,
            annualIncome: monthlyIncome * 12,
            totalRelief: 0,
            chargeableIncome: 0,
            annualTax: 0,
            monthlyPCB: 0
        };

        // Calculate total reliefs
        if (taxReliefs) {
            result.totalRelief = (taxReliefs.selfRelief || 0) +
                                (taxReliefs.spouseRelief || 0) +
                                (taxReliefs.childRelief || 0) +
                                (taxReliefs.parentRelief || 0) +
                                (taxReliefs.disabledRelief || 0) +
                                (taxReliefs.lifeInsuranceRelief || 0) +
                                (taxReliefs.epfRelief || 0) +
                                (taxReliefs.educationRelief || 0) +
                                (taxReliefs.medicalRelief || 0) +
                                (taxReliefs.otherReliefs || 0);
        } else {
            // Default self relief
            result.totalRelief = 9000;
        }

        // Calculate chargeable income
        result.chargeableIncome = Math.max(0, result.annualIncome - result.totalRelief);

        // Simplified Malaysian tax brackets (2026) - Progressive rates
        // This is approximate - backend will use exact LHDN tables
        let annualTax = 0;

        if (result.chargeableIncome <= 5000) {
            annualTax = 0;
        } else if (result.chargeableIncome <= 20000) {
            annualTax = (result.chargeableIncome - 5000) * 0.01;
        } else if (result.chargeableIncome <= 35000) {
            annualTax = 150 + (result.chargeableIncome - 20000) * 0.03;
        } else if (result.chargeableIncome <= 50000) {
            annualTax = 600 + (result.chargeableIncome - 35000) * 0.08;
        } else if (result.chargeableIncome <= 70000) {
            annualTax = 1800 + (result.chargeableIncome - 50000) * 0.13;
        } else if (result.chargeableIncome <= 100000) {
            annualTax = 4400 + (result.chargeableIncome - 70000) * 0.21;
        } else if (result.chargeableIncome <= 250000) {
            annualTax = 10700 + (result.chargeableIncome - 100000) * 0.24;
        } else if (result.chargeableIncome <= 400000) {
            annualTax = 46700 + (result.chargeableIncome - 250000) * 0.245;
        } else if (result.chargeableIncome <= 600000) {
            annualTax = 83450 + (result.chargeableIncome - 400000) * 0.25;
        } else if (result.chargeableIncome <= 1000000) {
            annualTax = 133450 + (result.chargeableIncome - 600000) * 0.26;
        } else {
            annualTax = 237450 + (result.chargeableIncome - 1000000) * 0.28;
        }

        result.annualTax = Math.round(annualTax * 100) / 100;
        result.monthlyPCB = Math.round((annualTax / 12) * 100) / 100;

        return result;
    }

    /**
     * Calculate gross income from earnings array
     * @param {Array} earnings - Array of earning items with amount property
     * @returns {number} Total gross income
     */
    calculateGrossIncome(earnings) {
        if (!earnings || !Array.isArray(earnings)) return 0;

        return earnings.reduce((sum, item) => {
            return sum + (parseFloat(item.amount) || 0);
        }, 0);
    }

    /**
     * Calculate total deductions from deductions array
     * @param {Array} deductions - Array of deduction items with amount property
     * @returns {number} Total deductions
     */
    calculateTotalDeductions(deductions) {
        if (!deductions || !Array.isArray(deductions)) return 0;

        return deductions.reduce((sum, item) => {
            return sum + (parseFloat(item.amount) || 0);
        }, 0);
    }

    /**
     * Calculate total employer contributions
     * @param {Array} contributions - Array of contribution items with amount property
     * @returns {number} Total employer contributions
     */
    calculateTotalEmployerContributions(contributions) {
        if (!contributions || !Array.isArray(contributions)) return 0;

        return contributions.reduce((sum, item) => {
            return sum + (parseFloat(item.amount) || 0);
        }, 0);
    }

    /**
     * Calculate net pay
     * @param {number} grossIncome - Total earnings
     * @param {number} totalDeductions - Total deductions
     * @returns {number} Net pay amount
     */
    calculateNetPay(grossIncome, totalDeductions) {
        return Math.max(0, grossIncome - totalDeductions);
    }

    /**
     * Calculate total employment cost (including employer contributions)
     * @param {number} grossIncome - Total earnings
     * @param {number} employerContributions - Total employer contributions
     * @returns {number} Total employment cost
     */
    calculateTotalEmploymentCost(grossIncome, employerContributions) {
        return grossIncome + employerContributions;
    }

    /**
     * Calculate pro-rated salary for partial month
     * @param {number} monthlySalary - Full monthly salary
     * @param {number} workingDays - Days worked in the month
     * @param {number} totalDaysInMonth - Total days in the month
     * @returns {number} Pro-rated salary amount
     */
    calculateProRatedSalary(monthlySalary, workingDays, totalDaysInMonth) {
        if (totalDaysInMonth === 0) return 0;
        return Math.round((monthlySalary / totalDaysInMonth * workingDays) * 100) / 100;
    }

    /**
     * Format currency for display
     * @param {number} amount - Amount to format
     * @param {string} currency - Currency code (default: MYR)
     * @returns {string} Formatted currency string
     */
    formatCurrency(amount, currency = 'MYR') {
        return new Intl.NumberFormat('en-MY', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    }

    /**
     * Calculate employee age from date of birth
     * @param {Date|string} dateOfBirth - Date of birth
     * @returns {number} Employee age in years
     */
    calculateAge(dateOfBirth) {
        if (!dateOfBirth) return 0;

        const dob = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age--;
        }

        return age;
    }

}

export default PayrollCalculator;
